import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Form,
  FormInstance,
  Input,
  Modal,
  Radio,
  Row,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { useAllRoles } from '@/core/domain/enterprise/Roles/Roles.hooks';
import { FormValues } from './../../../../pages/SettingsPages/ProfilesPage/ProfileDetailsPage/index';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  disableRoleInPorto,
  RoleSubGroup,
  threeRoles,
} from './profile.threeRoles';
import { Group } from '@/components/_common';
import capitalizeFirstLetter from '@/utils/functions/capitalizeFirstLetter';

interface Props {
  form: FormInstance<FormValues>;
  roleIds: number[];
  onChangeRoleIds(ids: number[]): void;
  creation?: boolean;
  isAdm?: boolean;
}

const ProfileForm = ({
  form,
  creation,
  roleIds,
  onChangeRoleIds,
  isAdm,
}: Props) => {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const [modalOpen, modalAction] = useDisclosure(creation);

  const allSelected = Form.useWatch('allSelected', form);

  const history = useHistory();

  const { data: roles, isFetching, dataUpdatedAt } = useAllRoles();

  const roleGroups = useMemo(() => threeRoles(roles), [dataUpdatedAt]);

  function handleCheckRole(subRole: RoleSubGroup, isChecked: boolean) {
    onChangeRoleIds(
      isChecked
        ? roleIds.filter((roleId) => roleId !== subRole.id)
        : [...roleIds, subRole.id],
    );
  }

  const sortByName = (a: { name: string }, b: { name: string }) => {
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
  };

  return (
    <Form form={form} style={{ marginTop: '16px' }}>
      <Skeleton active loading={isFetching}>
        <FloatingFormItem
          name="name"
          label={intl.formatMessage({ id: 'profiles.domain.name' })}
          rules={[defaultEmptyFieldError]}
        >
          <Input />
        </FloatingFormItem>

        <Row gutter={24} style={{ marginTop: 24, marginBottom: 24 }}>
          <Col flex={1}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              <FormattedMessage id="profiles.domain.roles" />
            </Typography.Title>
          </Col>

          <Col>
            <Group gap={24}>
              <Button
                onClick={() =>
                  onChangeRoleIds(roles?.map((role) => role.id) || [])
                }
              >
                Selecionar todos
              </Button>

              <Button onClick={() => onChangeRoleIds([])}>Remover todos</Button>
            </Group>
          </Col>
        </Row>

        <Collapse>
          {roleGroups?.sort(sortByName).map((roleGroup) => (
            <Collapse.Panel header={roleGroup.name} key={roleGroup.key}>
              {roleGroup.roles?.sort(sortByName).map((roleSubGroup) => {
                const isSubGroupChecked = roleSubGroup.roles?.every((subRole) =>
                  roleIds.includes(subRole.id),
                );
                const isIndeterminated = roleSubGroup.roles?.some((subRole) =>
                  roleIds.includes(subRole.id),
                );

                return (
                  <Collapse key={roleSubGroup.key} ghost>
                    <Collapse.Panel
                      className="profile-collapse-header"
                      key={roleSubGroup.key}
                      showArrow={!!roleSubGroup.roles?.length}
                      collapsible={
                        !!roleSubGroup.roles?.length ? 'header' : 'disabled'
                      }
                      header={
                        <Checkbox
                          checked={isSubGroupChecked}
                          disabled={isAdm}
                          indeterminate={isIndeterminated && !isSubGroupChecked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => {
                            if (isSubGroupChecked) {
                              onChangeRoleIds(
                                roleIds.filter(
                                  (roleId) =>
                                    !roleSubGroup.roles?.some(
                                      (subGroup) => subGroup.id === roleId,
                                    ),
                                ),
                              );
                            } else {
                              onChangeRoleIds([
                                ...roleIds.filter(
                                  (roleId) =>
                                    !roleSubGroup.roles?.some(
                                      (subGroup) => subGroup.id === roleId,
                                    ),
                                ),
                                ...(roleSubGroup.roles?.map(
                                  (subRole) => subRole.id,
                                ) || []),
                              ]);
                            }
                          }}
                        >
                          <span onClick={(e) => e.stopPropagation()}>
                            {capitalizeFirstLetter(roleSubGroup.name)}
                          </span>
                        </Checkbox>
                      }
                    >
                      <Space direction="vertical" style={{ marginLeft: 24 }}>
                        {roleSubGroup.roles?.sort(sortByName).map((subRole) => {
                          const isChecked = roleIds.includes(subRole.id);
                          return (
                            <Checkbox
                              key={subRole.id}
                              value={subRole.id}
                              checked={isChecked}
                              disabled={isAdm}
                              onChange={() =>
                                handleCheckRole(subRole, isChecked)
                              }
                            >
                              {capitalizeFirstLetter(subRole.name)}
                            </Checkbox>
                          );
                        })}
                      </Space>
                    </Collapse.Panel>
                  </Collapse>
                );
              })}
            </Collapse.Panel>
          ))}
        </Collapse>
      </Skeleton>

      <Modal
        open={modalOpen}
        title="Novo grupo"
        onOk={() => {
          if (allSelected) {
            onChangeRoleIds(
              roles
                ?.filter((role) => !disableRoleInPorto(role))
                ?.map((role) => role.id) || [],
            );
          } else {
            onChangeRoleIds([]);
          }
          modalAction.close();
        }}
        onCancel={() => history.push('/app/settings/profiles')}
      >
        <FloatingFormItem label="Nome do grupo" name="name">
          <Input />
        </FloatingFormItem>

        <Form.Item initialValue={true} name="allSelected">
          <Radio.Group>
            <Radio style={{ height: '100%' }} value={true}>
              Inicializar grupo com todas as permissões concedidas
            </Radio>

            <Radio style={{ height: '100%' }} value={false}>
              Inicializar grupo com todas as permissões negadas
            </Radio>
          </Radio.Group>
        </Form.Item>
      </Modal>
    </Form>
  );
};

export default ProfileForm;
